import React from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import Indicator from '../condition/indicators/types/Indicator'
import { COMPOSITE_LIMITS, CompositeRef, SourceField } from '../../../types/composite'
import { nodeOutputs } from '../../../common/utils/composite'
import { DraftStep, SOURCE_FIELDS, outputLabel, sourceFieldLabel, stepLabel } from './types'

const CLEAR_VALUE = '__chart__'

type RefOption = { value: string; label: string }

const encodeStep = (stepId: string, output?: string) => `step|${stepId}|${output ?? ''}`
const encodeSource = (field: SourceField) => `src|${field}`

const encodeRef = (ref: CompositeRef): string =>
  'node' in ref ? encodeStep(ref.node, ref.output) : encodeSource(ref.source)

interface StepRefSelectProps {
  steps: DraftStep[]
  beforeIndex: number // step options come from steps[0..beforeIndex-1]
  value?: CompositeRef
  onChange: (ref?: CompositeRef) => void
  getIndicatorByValue: (value: string) => Indicator | undefined
  /** Chart fields (close, volume, …) as directly pickable options. Off for
   * outputs, which expose computed steps only. */
  allowSources?: boolean
  allowClear?: boolean
  clearLabel?: string
  label?: string
}

const StepRefSelect: React.FC<StepRefSelectProps> = ({
  steps,
  beforeIndex,
  value,
  onChange,
  getIndicatorByValue,
  allowSources = true,
  allowClear = false,
  clearLabel = 'Chart price',
  label = 'Input',
}) => {
  const stepOptions: RefOption[] = []
  steps.slice(0, beforeIndex).forEach((step, index) => {
    const base = stepLabel(step, index, getIndicatorByValue)
    const outputs =
      step.kind === 'indicator' && step.indicatorName
        ? nodeOutputs(step.indicatorName, getIndicatorByValue)
        : ['value']
    if (outputs.length > 1) {
      outputs.forEach((output) =>
        stepOptions.push({
          value: encodeStep(step.id, output),
          label: `${base} · ${outputLabel(output)}`,
        })
      )
    } else {
      stepOptions.push({ value: encodeStep(step.id), label: base })
    }
  })

  const selected = value ? encodeRef(value) : CLEAR_VALUE
  const selectedIsKnown =
    selected === CLEAR_VALUE ||
    stepOptions.some((option) => option.value === selected) ||
    (allowSources && selected.startsWith('src|'))

  const handleChange = (raw: string) => {
    if (raw === CLEAR_VALUE) {
      onChange(undefined)
      return
    }
    const offset = value?.offset
    if (raw.startsWith('src|')) {
      const ref: CompositeRef = { source: raw.slice(4) as SourceField }
      if (offset) ref.offset = offset
      onChange(ref)
      return
    }
    const [, node, output] = raw.split('|')
    const ref: CompositeRef = { node }
    if (output) ref.output = output
    if (offset) ref.offset = offset
    onChange(ref)
  }

  const handleOffsetChange = (rawOffset: number) => {
    if (!value) return
    const offset = Math.floor(rawOffset || 0)
    const ref: CompositeRef =
      'node' in value
        ? { node: value.node, ...(value.output ? { output: value.output } : {}) }
        : { source: value.source }
    if (offset > 0) ref.offset = offset
    onChange(ref)
  }

  return (
    <Box display="flex" gap={1} alignItems="center">
      <FormControl size="small" sx={{ minWidth: 200 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={selectedIsKnown ? selected : CLEAR_VALUE}
          onChange={(e) => handleChange(e.target.value)}
        >
          {allowClear && <MenuItem value={CLEAR_VALUE}>{clearLabel}</MenuItem>}
          {allowSources && <ListSubheader>Chart data</ListSubheader>}
          {allowSources &&
            SOURCE_FIELDS.map((field) => (
              <MenuItem key={encodeSource(field)} value={encodeSource(field)}>
                {sourceFieldLabel(field)}
              </MenuItem>
            ))}
          {allowSources && stepOptions.length > 0 && <ListSubheader>Steps</ListSubheader>}
          {stepOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {value && (
        <TextField
          size="small"
          type="number"
          label="Candles back"
          value={value.offset ?? 0}
          onChange={(e) => handleOffsetChange(Number(e.target.value))}
          inputProps={{ min: 0, max: COMPOSITE_LIMITS.maxOffset, step: 1 }}
          sx={{ width: 120 }}
        />
      )}
    </Box>
  )
}

export default StepRefSelect
